import { Transform, Type } from 'class-transformer';

import { CRYPTOCLOUD_USDT_NETWORKS } from '../../constants';
import { ApiProperty, IntersectionType, PickType } from '../../codegen';
import { EBillingTaskStatus } from '../../enums';
import { EDodoDepositPaymentMethod } from '../../enums/billing/EDodoDepositPaymentMethod';
import {
  EVisa2PayCardType,
  VISA2PAY_CARD_TYPES,
} from '../../enums/billing/EVisa2PayCardType';
import {
  IsEmail,
  IsInt,
  IsLuhnCardNumber,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  Min,
  Matches,
  Length,
  ValidateNested,
} from '../../validators';
import { IsIn } from '../../validators';
import {
  normalizeCardDepositExpiryMonth,
  normalizeCardDepositExpiryYear,
} from '../../helpers/normalizeCardDepositCardDetails';
import { normalizeCardNumberDigits } from '../../helpers/detectVisa2PayCardType';

import { BillingTask, SubscriptionTaskData } from './task.entity';

const MIN_DEPOSIT_USD = 5;
export const MIN_DEPOSIT_COINS = MIN_DEPOSIT_USD * 100;

/** Returned when Visa2Pay accepted the charge but HTTP response timed out. */
export const CARD_DEPOSIT_ASYNC_PROCESSING_RESULT =
  '__CARD_DEPOSIT_PROCESSING__';
const MIN_DEPOSIT_MESSAGE_KEY = 'custom.minimumDepositAmount';

const MIN_WITHDRAW_USD = 50;
const MIN_WITHDRAW_COINS = MIN_WITHDRAW_USD * 100;
const MIN_WITHDRAW_MESSAGE_KEY = 'custom.minimumWithdrawalAmount';

export class CreateCoinPurchaseTaskDto extends PickType(BillingTask, [
  'billingPacket',
  'quantity',
]) {
  @ApiProperty({ required: false })
  @IsString({ groups: ['error'] })
  redirectUrl?: string;
}

export class DiscountInfoDto extends PickType(SubscriptionTaskData, [
  'action',
  'discount',
  'after',
  'previousPlan',
  'planName',
]) {}

export class CreateSubscriptionTaskDto extends IntersectionType(
  PickType(BillingTask, ['billingPacket', 'quantity']),
  PickType(SubscriptionTaskData, ['entityName', 'entityId']),
) {}

export class ConfirmSubscriptionTaskDto {
  @ApiProperty({ required: false })
  @IsString({ groups: ['error'] })
  redirectUrl?: string;
}

export class BillingTaskReturnDto extends PickType(BillingTask, [
  'id',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'context',
  'price',
  'billingTaskType',
  'status',
  'coins',
  'billingPacket',
  'source',
]) {}

export class PgBillingTaskPayloadDto {
  id: number;
  old: { status: EBillingTaskStatus };
  new: { status: EBillingTaskStatus };
}

export class PgAccountPayloadDto {
  id: number;
  old: { balance?: number };
  new: { balance?: number };
}

export class PgSubscriptionPayloadDto {
  id: number;
  old: Record<string, never>;
  new: Record<string, never>;
}

export class BillingGenerationDataDto {
  @IsNumber({}, { groups: ['error'] })
  userIdDebited: number;

  @IsNumber({}, { groups: ['error'] })
  userIdCreated: number;

  @IsNumber({}, { groups: ['error'] })
  coins: number;

  @IsNumber({}, { groups: ['error'] })
  cost?: number;

  @IsNumber({}, { groups: ['error'] })
  coinsPerOne?: number;

  @IsNumber({}, { groups: ['error'] })
  coinsReturned?: number;
}

export class BillingPriceDto {
  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  id: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  price: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  pricePrevious: number;

  @ApiProperty()
  @IsNumber({}, { groups: ['error'] })
  coins: number;
}

export class CreateCryptoDepositTaskDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(MIN_DEPOSIT_COINS, {
    groups: ['error'],
    messageKey: MIN_DEPOSIT_MESSAGE_KEY,
  })
  coins: number;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsIn(CRYPTOCLOUD_USDT_NETWORKS, { groups: ['error'] })
  currencyCode: string;

  @ApiProperty({ required: false })
  @IsString({ groups: ['error'] })
  redirectUrl?: string;
}

export class CreateCryptoWithdrawTaskDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(MIN_WITHDRAW_COINS, {
    groups: ['error'],
    messageKey: MIN_WITHDRAW_MESSAGE_KEY,
  })
  coins: number;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsIn(CRYPTOCLOUD_USDT_NETWORKS, { groups: ['error'] })
  currencyCode: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  toAddress: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  code: string;
}

export class CreatePaypalDepositTaskDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(MIN_DEPOSIT_COINS, {
    groups: ['error'],
    messageKey: MIN_DEPOSIT_MESSAGE_KEY,
  })
  coins: number;

  @ApiProperty({ required: false })
  @IsOptional({ groups: ['error'] })
  @IsString({ groups: ['error'] })
  redirectUrl?: string;
}

export class CapturePaypalDepositTaskDto {
  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  orderId: string;
}

export class CreatePaypalWithdrawTaskDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(MIN_WITHDRAW_COINS, {
    groups: ['error'],
    messageKey: MIN_WITHDRAW_MESSAGE_KEY,
  })
  coins: number;

  @ApiProperty()
  @IsEmail({}, { groups: ['error'] })
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  paypalEmail: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  code: string;
}

export class MockCryptoBillingTaskActionDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(1, { groups: ['error'] })
  billingTaskId: number;
}

export class MockPaypalBillingTaskActionDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(1, { groups: ['error'] })
  billingTaskId: number;
}

export class CardDepositBillingDetailsDto {
  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  firstName: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  lastName: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  phone: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  address: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  city: string;

  @ApiProperty({ required: false })
  @IsOptional({ groups: ['error'] })
  @IsString({ groups: ['error'] })
  state?: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @Length(2, 2, { groups: ['error'] })
  country: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @IsNotEmpty({ groups: ['error'] })
  zipCode: string;
}

export class CardDepositCardDetailsDto {
  @ApiProperty()
  @IsIn(VISA2PAY_CARD_TYPES, { groups: ['error'] })
  cardType: EVisa2PayCardType;

  @ApiProperty()
  @Transform(({ value }) =>
    typeof value === 'string' ? normalizeCardNumberDigits(value) : value,
  )
  @IsString({ groups: ['error'] })
  @Matches(/^\d{13,19}$/, { groups: ['error'] })
  @IsLuhnCardNumber({ groups: ['error'] })
  cardNumber: string;

  @ApiProperty()
  @IsString({ groups: ['error'] })
  @Matches(/^\d{3,4}$/, { groups: ['error'] })
  cardCVV: string;

  @ApiProperty()
  @Transform(({ value }) =>
    typeof value === 'string' ? normalizeCardDepositExpiryYear(value) : value,
  )
  @IsString({ groups: ['error'] })
  @Matches(/^\d{4}$/, { groups: ['error'] })
  cardExpYear: string;

  @ApiProperty()
  @Transform(({ value }) =>
    typeof value === 'string' ? normalizeCardDepositExpiryMonth(value) : value,
  )
  @IsString({ groups: ['error'] })
  @Matches(/^(0[1-9]|1[0-2])$/, { groups: ['error'] })
  cardExpMonth: string;
}

export class CreateCardDepositTaskDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(MIN_DEPOSIT_COINS, {
    groups: ['error'],
    messageKey: MIN_DEPOSIT_MESSAGE_KEY,
  })
  coins: number;

  @ApiProperty({ required: false })
  @IsOptional({ groups: ['error'] })
  @IsString({ groups: ['error'] })
  redirectUrl?: string;

  @ApiProperty({ required: false })
  @IsOptional({ groups: ['error'] })
  @IsIn(Object.values(EDodoDepositPaymentMethod), { groups: ['error'] })
  paymentMethod?: EDodoDepositPaymentMethod;

  @ApiProperty()
  @ValidateNested({ groups: ['error'] })
  @Type(() => CardDepositBillingDetailsDto)
  billing: CardDepositBillingDetailsDto;

  @ApiProperty()
  @ValidateNested({ groups: ['error'] })
  @Type(() => CardDepositCardDetailsDto)
  card: CardDepositCardDetailsDto;
}
export class MockCardBillingTaskActionDto {
  @ApiProperty()
  @IsInt({ groups: ['error'] })
  @Min(1, { groups: ['error'] })
  billingTaskId: number;
}
